import { Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { Card, Dialog, InfoTooltip } from 'components';
import React, { ReactNode, useState, forwardRef } from 'react';
import { EditOutlined, InfoOutlined } from '@mui/icons-material';
import { TextFieldProps } from '@mui/material/TextField/TextField';
import { useIsMobile } from 'hooks';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete_icon.svg';

type OverviewMaxUnitPriceCardProps = {
  title: string;
  tooltipText: ReactNode;
  value: string | null;
  priceModelText: string;
  priceModelTooltipText: ReactNode;
  inputProps?: TextFieldProps;
  onSubmit?: (value: string | null) => void;
  initialFieldValue?: string | number;
  infoMessage?: ReactNode;
};

export const OverviewMaxUnitPriceCard = forwardRef<HTMLDivElement, OverviewMaxUnitPriceCardProps>(
  function OverviewMaxUnitPriceCard(
    {
      title,
      tooltipText,
      value,
      priceModelText,
      priceModelTooltipText,
      inputProps,
      onSubmit,
      infoMessage,
      initialFieldValue,
    }: OverviewMaxUnitPriceCardProps,
    ref
  ) {
    const [fieldValue, setFieldValue] = useState<string>(String(initialFieldValue) ?? '');
    const [isEditable, setIsEditable] = useState<boolean>(false);
    const { isMobile } = useIsMobile();
    const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);
    const showDeleteCampaignDialog = () => {
      setIsConfirmDeleteDialogOpen(true);
    };

    const hideDeleteCampaignDialog = () => {
      setIsConfirmDeleteDialogOpen(false);
    };

    const InfoMessage = infoMessage ? (
      <Paper
        sx={{
          backgroundColor: 'warning.light',
          px: 4,
          py: 1.5,
          borderRadius: 2 / 3,
          mt: 4,
          mb: 4,
        }}
      >
        <Stack direction="row">
          <InfoOutlined sx={{ my: 2, mr: 3, color: 'warning.dark' }} />
          <Stack sx={{ py: 2 }} gap={1}>
            {infoMessage}
          </Stack>
        </Stack>
      </Paper>
    ) : null;

    return (
      <Card
        sx={{
          height: 'inherit',
          width: isMobile ? '100%' : '594px',
        }}
      >
        <ClickAwayListener
          onClickAway={() => {
            setIsEditable(false);
            setFieldValue('');
          }}
        >
          <div ref={ref}>
            <Stack direction={isMobile ? 'column' : 'row'}>
              <Stack direction="column" flexGrow={1}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="h4" mb={2}>
                    {title}
                  </Typography>
                  <Box sx={{ display: 'inline-block', transform: 'translateY(-4px)', marginLeft: '8px' }}>
                    <InfoTooltip placement="top" text={tooltipText} />
                  </Box>
                </Stack>
                <Typography
                  variant="body2"
                  color={infoMessage && value ? 'warning.dark' : undefined}
                  fontWeight={infoMessage && value ? 700 : undefined}
                >
                  {value ?? 'Kein Max. Preis festgelegt'}
                </Typography>
                <Stack direction="row" alignItems="center" justifyContent="start" mt={4} mb={2}>
                  {isEditable ? (
                    <Stack width={isMobile ? '100%' : 'inherit'}>
                      <TextField
                        {...inputProps}
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={fieldValue}
                        onChange={(e) => setFieldValue(e.target.value)}
                      />
                      <Stack direction="row" spacing={1} mt={2} justifyContent="space-between">
                        <Button
                          size="small"
                          fullWidth
                          variant="outlined"
                          onClick={() => {
                            setIsEditable(false);
                            setFieldValue('');
                          }}
                          sx={{ marginLeft: '8px', height: 'auto' }}
                        >
                          Abbrechen
                        </Button>
                        <Button
                          fullWidth
                          size="small"
                          variant="contained"
                          onClick={() => {
                            onSubmit?.(fieldValue);

                            setIsEditable(false);
                            setFieldValue('');
                          }}
                          sx={{ marginTop: '8px', height: 'auto' }}
                        >
                          Speichern
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <>
                      {inputProps && !inputProps.disabled && !isEditable && value !== null ? (
                        <Button
                          onClick={() => {
                            showDeleteCampaignDialog();
                          }}
                          sx={{ marginLeft: '8px', height: 'auto' }}
                          startIcon={<DeleteIcon />}
                        >
                          Entfernen
                        </Button>
                      ) : null}
                      {inputProps && !inputProps.disabled && !isEditable ? (
                        <Button
                          onClick={() => {
                            setIsEditable(true);
                          }}
                          sx={{ marginLeft: '8px', height: 'auto' }}
                          startIcon={<EditOutlined />}
                        >
                          Anpassen
                        </Button>
                      ) : null}
                    </>
                  )}
                </Stack>
                {isEditable && isMobile ? InfoMessage : null}
              </Stack>
              <Stack direction="column" flexGrow={1}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="h4" mb={2}>
                    Preismodell
                  </Typography>
                  <Box sx={{ display: 'inline-block', transform: 'translateY(-4px)', marginLeft: '8px' }}>
                    <InfoTooltip placement="top" text={priceModelTooltipText} />
                  </Box>
                </Stack>
                <Typography variant="body2">{priceModelText}</Typography>
              </Stack>
            </Stack>
            {isEditable && !isMobile ? InfoMessage : null}
          </div>
        </ClickAwayListener>
        <Dialog
          open={isConfirmDeleteDialogOpen}
          title="Max. Preis pro Engagement entfernen"
          content="Es kann bis zu 48 Stunden dauern, bis die verbesserte Performance sichtbar wird."
          primaryActionTitle="Ok, verstanden"
          secondaryActionTitle="Abbrechen"
          onSecondaryActionClick={hideDeleteCampaignDialog}
          onPrimaryActionClick={() => {
            onSubmit?.(null);

            setIsConfirmDeleteDialogOpen(false);
          }}
        />
      </Card>
    );
  }
);
